import { AuthHeading, AuthShell, AuthStack, ChoiceList, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { errorOf } from "../api";
import { adminCompanies, landingFor } from "../guards";
import { chooseCompany, meQuery } from "../session";
import { useSignOut } from "../use-sign-out";
import { HELP_LINE } from "./sign-in";

/** Choose a company (`VHsi2`): the companies where the person is staff. */
export function ChooseCompanyPage(): ReactNode {
  const { next } = useSearch({ from: "/choose-company" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const { signOut, pending: signingOut } = useSignOut();
  const choose = useMutation({
    mutationFn: chooseCompany,
    onSuccess: async (updated) => {
      queryClient.setQueryData(meQuery.queryKey, updated);
      await navigate({ href: landingFor(updated, next) });
    },
    onError: () => queryClient.invalidateQueries({ queryKey: meQuery.queryKey }),
  });

  if (!me) return null;
  const companies = adminCompanies(me);
  const refused = choose.error ? errorOf(choose.error).code === "COMPANY_NOT_ALLOWED" : false;

  return (
    <AuthShell product="Admin console" helpLine={HELP_LINE}>
      <AuthStack>
        <AuthHeading
          title="Choose a company"
          description={`Signed in as ${me.user.email ?? me.user.name}. You can switch later from the sidebar.`}
        />
        {choose.error ? (
          <Alert tone="danger" title={refused ? "You can't open that company" : "That didn't work"}>
            {refused
              ? "Your access there has changed. Choose another company, or ask an administrator."
              : "The console couldn't switch company. Try again."}
          </Alert>
        ) : null}
        {companies.length === 0 ? (
          <Alert tone="danger" title="No company to open">
            You aren't staff in any company any more. Ask an administrator to add you again.
          </Alert>
        ) : (
          <ChoiceList
            label="Your companies"
            choices={companies.map((c) => ({
              id: c.id,
              mark: c.slug.slice(0, 3).toUpperCase(),
              title: c.name,
              subtitle: c.slug,
            }))}
            currentId={me.active_company?.id}
            pendingId={choose.isPending ? (choose.variables ?? null) : null}
            onChoose={(id) => choose.mutate(id)}
          />
        )}
        <TextButton onClick={signOut} disabled={signingOut}>
          Sign out
        </TextButton>
      </AuthStack>
    </AuthShell>
  );
}
